// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Base API Configuration
 * Provides a centralized fetch wrapper with error handling and common configurations.
 *
 * Token acquisition is async (MSAL's acquireTokenSilent) — the wrapper
 * awaits it before every request so the Authorization header always
 * carries a fresh access token.
 */

import { getApiUrl } from '../utils/constants';
import { getTokenAsync } from '../config/authConfig';
import { removeUserData } from './tokenStorage';
import { useShellStore } from '../stores/shellStore';
import { useSessionGuardStore } from '../stores/sessionGuardStore';

/**
 * Error thrown for non-2xx responses, carrying the HTTP status so callers
 * can branch on it (e.g. treat 404 as "not found" rather than a failure).
 *
 * A class rather than an interface so react-query's retry predicate can use
 * `instanceof` to tell "the server said no" from a transient network fault
 * (ADR-0008, Decision 4). Every non-2xx path in this module throws one,
 * including the 401 branches — a retried 401 would re-fire the session lock,
 * which is exactly what the predicate exists to prevent.
 */
export class ApiError extends Error {
  status?: number;
  /** The BFF's `X-Request-Id` for this call: one value that finds the request
      and its upstream calls in the BFF log (back/app/core/request_log.py). */
  requestId?: string;
  /** The BFF's machine-readable `code`, when it sent one (e.g. an upstream
      refusal like `LOCAL_ACCOUNT_EXISTS`), for callers that branch on it. */
  code?: string;
  /** The BFF's `details` object, when it sent one (e.g. `existing_user_id`). */
  details?: Record<string, unknown>;
  /** The upstream's own message, without the code and trace id `message`
      carries. IAM writes its refusals to be shown to users as-is. */
  upstreamMessage?: string;
  /** For a 403 the BFF worded as "Not authorized for <action>": that action
      (e.g. `twin.sites.read`). Kept for logs and support; never shown to a user
      (AB#35577), whose `message` is NO_ACCESS_MESSAGE instead. */
  deniedAction?: string;

  constructor(message: string, status?: number, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
  }
}

/**
 * What a user reads when a permission check refuses them (AB#35577). The BFF's
 * own wording is `Not authorized for twin.sites.read`: an internal IAM action
 * name that tells an operator nothing and exposes how authorization works.
 */
export const NO_ACCESS_MESSAGE =
  "You don't have access to this. Your account doesn't include the role it needs. Ask an administrator if you think you should have access.";

const PERMISSION_DENIAL = /^Not authorized for ([\w.:*-]+)\.?$/i;

/** True for the message a page was given for a permission refusal (a 403). */
export const isNoAccessMessage = (message: string | null | undefined): boolean =>
  message === NO_ACCESS_MESSAGE;

/**
 * Downstream services return their own error envelope
 * (`{status, errorCode, message, details, path, correlationId}`) and the BFF
 * relays the body verbatim as the FastAPI `detail` — see `_request` in
 * `back/app/clients/base.py`. Without unwrapping it, the UI renders the whole
 * JSON blob at the user. Pull out the human-readable `message`.
 */
function unwrapUpstreamEnvelope(detail: unknown): string | null {
  let envelope = detail;
  if (typeof envelope === 'string') {
    const trimmed = envelope.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
      envelope = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (envelope && typeof envelope === 'object') {
    const { message } = envelope as { message?: unknown };
    if (typeof message === 'string' && message) return message;
  }
  return null;
}

/**
 * FastAPI error `detail` may be a string, an array of validation issues, or
 * an object. Normalize to readable text so callers never render
 * "[object Object]" — or a raw JSON error envelope.
 */
function detailToMessage(detail: unknown, fallback: string): string {
  const upstream = unwrapUpstreamEnvelope(detail);
  if (upstream) return upstream;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map(entry => {
      if (entry && typeof entry === 'object' && 'msg' in entry) {
        const issue = entry as { msg: string; loc?: unknown[] };
        const loc = Array.isArray(issue.loc) ? issue.loc.filter(l => l !== 'body').join('.') : '';
        return loc ? `${loc}: ${issue.msg}` : issue.msg;
      }
      return JSON.stringify(entry);
    });
    if (parts.length > 0) return parts.join('; ');
  }
  if (detail && typeof detail === 'object') {
    const serialized = JSON.stringify(detail);
    if (serialized && serialized !== '{}') return serialized;
  }
  return fallback;
}

/**
 * Pick the message out of an error response body. Prefers FastAPI's `detail`,
 * falling back to the body itself so a bare upstream envelope (no `detail`
 * wrapper) is still unwrapped rather than rendered raw.
 */
function responseToMessage(errorData: unknown, fallback: string): string {
  const detail = (errorData as { detail?: unknown } | null)?.detail;
  return detailToMessage(detail !== undefined ? detail : errorData, fallback);
}

export interface ApiOptions extends RequestInit {
  /**
   * Whether a 401 from this request should be read as the user's session
   * having expired. Default true.
   *
   * Set false for an *optional* resource — one whose caller already tolerates
   * failure and degrades gracefully. Without it, a single unauthorized
   * peripheral service tears down a perfectly good session: the expiry is
   * raised as a side effect inside this helper, so a caller's `try/catch`
   * around the request cannot undo it. That is how one 401 on a linked SOP
   * artifact was logging users out of the whole app on the alarm detail page.
   *
   * The request still rejects with an ApiError either way — only the global
   * session teardown is suppressed.
   *
   * A function decides per response from the BFF's `code` on the 401.
   */
  endSessionOn401?: boolean | ((code: string | undefined) => boolean);
}

/** The BFF's 403 `code` for a deactivated account (AB#35973). */
export const ACCOUNT_DISABLED = 'ACCOUNT_DISABLED';

/**
 * For callers that `fetch` directly (streams) instead of going through
 * `apiRequest`: locks with the deactivated overlay when `response` is the
 * BFF's 403 ACCOUNT_DISABLED. Reads a clone, so the body stays readable.
 */
export const lockIfAccountDisabled = async (response: Response): Promise<void> => {
  if (response.status !== 403) return;
  const body = (await response
    .clone()
    .json()
    .catch(() => null)) as { code?: unknown } | null;
  if (body?.code === ACCOUNT_DISABLED) useSessionGuardStore.getState().lock('account_disabled');
};

/**
 * Base fetch wrapper with error handling
 * @param endpoint - API endpoint (relative path)
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns Parsed JSON response
 */
export const apiRequest = async <T = unknown>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> => {
  const { endSessionOn401 = true, ...fetchOptions } = options;
  const url = getApiUrl(endpoint);

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  let tokenAttached = false;
  try {
    const token = await getTokenAsync();
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
      tokenAttached = true;
    }
  } catch {
    throw new Error('Not authenticated');
  }

  const config: RequestInit = {
    ...fetchOptions,
    headers: {
      ...defaultHeaders,
      ...(fetchOptions.headers as Record<string, string>),
    },
  };

  let response: Response;
  try {
    response = await fetch(url, config);
  } catch (networkError) {
    console.error('[api] Network request failed', networkError);
    useShellStore.getState().setBackendUnreachable(true);
    throw new ApiError("Can't reach the backend service right now.", undefined);
  }
  useShellStore.getState().setBackendUnreachable(false);

  try {
    if (response.status === 401) {
      const errorData = await response.json().catch(() => ({}));
      const { code } = (errorData ?? {}) as { code?: unknown };
      const errorCode = typeof code === 'string' && code ? code : undefined;
      const endSession =
        typeof endSessionOn401 === 'function' ? endSessionOn401(errorCode) : endSessionOn401;
      if (!endSession) {
        console.warn(`[api] ${endpoint} returned 401 — treating as unavailable, not expiry`);
        const apiError = new ApiError(
          responseToMessage(errorData, 'Not authorized for this resource.'),
          401
        );
        apiError.code = errorCode;
        throw apiError;
      }
      removeUserData();
      if (tokenAttached) {
        useSessionGuardStore.getState().lock('unauthorized');
      }
      throw new ApiError(
        responseToMessage(errorData, 'Session expired. Please log in again.'),
        response.status
      );
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const rawMessage = responseToMessage(errorData, `HTTP Error ${response.status}`);
      const denial = response.status === 403 ? PERMISSION_DENIAL.exec(rawMessage.trim()) : null;
      const apiError = new ApiError(
        denial ? NO_ACCESS_MESSAGE : rawMessage,
        response.status,
        response.headers?.get('X-Request-Id') ?? undefined
      );
      if (denial) apiError.deniedAction = denial[1];
      const { code, details, message } = (errorData ?? {}) as {
        code?: unknown;
        details?: unknown;
        message?: unknown;
      };
      if (typeof code === 'string' && code) apiError.code = code;
      if (typeof message === 'string' && message) apiError.upstreamMessage = message;
      if (details && typeof details === 'object' && !Array.isArray(details)) {
        apiError.details = details as Record<string, unknown>;
      }
      if (response.status === 403 && apiError.code === ACCOUNT_DISABLED) {
        useSessionGuardStore.getState().lock('account_disabled');
      }
      throw apiError;
    }

    if (response.status === 204) {
      return null as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    // The 401 branches above throw before reading the header; fill it here so
    // every ApiError from a response carries the id.
    if (error instanceof ApiError && !error.requestId) {
      error.requestId = response.headers?.get('X-Request-Id') ?? undefined;
    }
    const requestId = error instanceof ApiError && error.requestId;
    console.error(
      requestId ? `API Request Error [request ${requestId}]:` : 'API Request Error:',
      error
    );
    throw error;
  }
};

/**
 * GET request helper
 */
export const get = <T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'GET',
  });
};

/**
 * POST request helper
 */
export const post = <T = unknown>(
  endpoint: string,
  data?: unknown,
  options: ApiOptions = {}
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * PUT request helper
 */
export const put = <T = unknown>(
  endpoint: string,
  data?: unknown,
  options: ApiOptions = {}
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * PATCH request helper
 */
export const patch = <T = unknown>(
  endpoint: string,
  data?: unknown,
  options: ApiOptions = {}
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data),
  });
};

/**
 * DELETE request helper
 */
export const del = <T = unknown>(endpoint: string, options: ApiOptions = {}): Promise<T> => {
  return apiRequest<T>(endpoint, {
    ...options,
    method: 'DELETE',
  });
};
